import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { pathToRegexp } from 'path-to-regexp';
import { any, equals } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import { getLabelMessage } from '@gef-ui/localization/messages';
import { SidebarMenuItem } from '@gef-ui/material/components/Sidebar';

import { resetAppState } from 'features/common/app/actions';
import { getInternalLinks } from 'features/other/leftMenu/selectors';
import { getAgencyManagersAndSubordinates } from 'features/views/settings/marketPlaceAgencies/selectors';
import { getRoutePath } from 'utils/routeHelper';

import { useConfirmBeforeLeaveNavigation } from '../hooks';

export const InternalLinks = ({ onItemClick }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const internalLinks = useSelector(getInternalLinks);
	const marketPlaceAgencyManagersAndSubordinates = useSelector(getAgencyManagersAndSubordinates);

	const handleContinueRedirect = useCallback(
		({ to, onClickFns, screenId }) => {
			if (onItemClick) {
				onItemClick();
			}

			dispatch(resetAppState());
			navigate(getRoutePath(to));

			// logic for clicking on sidebarMenuItem to redirect while already being on that page (mainly for resetting, refetching etc.)
			// since the components contain fetch logic inside them and they are already mounted
			// we need to dispatch all "initial" actions separately

			if (!isNilOrEmpty(onClickFns) && any(equals(window.screenId))(screenId)) {
				onClickFns.forEach((fn) => dispatch(fn));
			}
		},
		[dispatch, navigate, onItemClick],
	);

	const [confirmBeforeRedirectDialog, openIfConditionMet] = useConfirmBeforeLeaveNavigation({
		condition: !isNilOrEmpty(marketPlaceAgencyManagersAndSubordinates),
		onOk: (values) => handleContinueRedirect(values),
	});

	const handleOnClick = useCallback(
		(item) => () => {
			if (pathname === '/settings') {
				openIfConditionMet({ to: item.link, onClickFns: item.onClick, screenId: item.screenId });
			} else {
				handleContinueRedirect({
					to: item.link,
					onClickFns: item.onClick,
					screenId: item.screenId,
				});
			}
		},
		[handleContinueRedirect, openIfConditionMet, pathname],
	);

	const handleActive = useCallback((activeForAliases) => {
		return any((alias) => {
			const re = pathToRegexp(getRoutePath(alias));
			return !!re.exec(location.pathname);
		}, activeForAliases);
	}, []);

	return (
		<>
			{confirmBeforeRedirectDialog}
			{internalLinks &&
				internalLinks.map((item, i) => {
					return (
						<AccessControl key={i} accessLevel={item.aclItems}>
							<SidebarMenuItem
								title={getLabelMessage(item.label)}
								onClick={handleOnClick(item)}
								active={handleActive(item.activeFor)}
								icon={item.icon}
							/>
						</AccessControl>
					);
				})}
		</>
	);
};

InternalLinks.propTypes = {
	onItemClick: PropTypes.func,
};

export default InternalLinks;
